import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useThemeContext } from "./ThemeContext";
import { lazy, Suspense, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { X, Sparkles, GripHorizontal, Maximize2 } from "lucide-react";
// Lazy: the chat thread only renders once the panel is opened, so keep it out of the main bundle.
const ConciergePanel = lazy(() => import("./ConciergePanel"));
import { useConciergeStore } from "@/store/conciergeStore";
import { useAuth } from "@/context/AuthContext";
import type { ConciergeVenue } from "@/lib/api/concierge";

import lucyImg from "@/assets/76eb55e8cd41f31edd0583e895edab2a3f31d888.webp";
import aceImg from "@/assets/de020e96d16b7138fb9827e48b2ff3ef44975fca.webp";
import danImg from "@/assets/f2ac2aa081ce84bb14f15e8907553634c808807e.webp";
import oliviaImg from "@/assets/16fc03d244f044a0bfec2c289acd1c8d322851d5.webp";

const CHARACTER_IMAGE_MAP: Record<string, string> = {
  lucy: lucyImg,
  ace: aceImg,
  dan: danImg,
  olivia: oliviaImg,
};

const AVATAR_URL =
  "/images/stock/photo-1673255745677-e36f618550d1.webp";

interface SelectedCharacter {
  id: string;
  name: string;
  title: string;
  image: string;
  specialty: string;
  gradient: string;
  glowColor: string;
  accentColor: string;
}

export default function AIConcierge() {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pulseRing, setPulseRing] = useState(true);
  const [selectedChar, setSelectedChar] = useState<SelectedCharacter | null>(
    null
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { isLight, isGlassy } = useThemeContext();
  const { isAuthenticated, sessionReady } = useAuth();

  // --- Load selected character from localStorage ---
  useEffect(() => {
    const stored = localStorage.getItem("venuelista_selected_character");
    if (stored) {
      try {
        setSelectedChar(JSON.parse(stored));
      } catch {}
    }
  }, []);

  // Re-check on route changes (in case user just selected a character)
  useEffect(() => {
    const stored = localStorage.getItem("venuelista_selected_character");
    if (stored) {
      try {
        setSelectedChar(JSON.parse(stored));
      } catch {}
    }
  }, [location.pathname]);

  // Derive the active concierge identity
  const conciergeAvatar = (selectedChar?.id && CHARACTER_IMAGE_MAP[selectedChar.id]) || AVATAR_URL;
  const conciergeName = selectedChar?.name || "Luna";
  const conciergeTitle = selectedChar?.title || "AI Concierge";
  const conciergeAccent = selectedChar?.accentColor || "#a855f7";
  const characterKey = selectedChar?.id || null;

  // --- Shared conversation state (also used by the maximized /chat page) ---
  // messages/isSending/sendMessage are read directly by ConciergePanel now.
  const ensureGreeting = useConciergeStore((s) => s.ensureGreeting);
  const setCharacter = useConciergeStore((s) => s.setCharacter);
  const loadPersistedHistory = useConciergeStore((s) => s.loadPersistedHistory);
  const cancel = useConciergeStore((s) => s.cancel);

  useEffect(() => {
    if (!isAuthenticated) return;
    setCharacter(characterKey);
    // Try resuming a logged-in member's saved conversation first — ensureGreeting is a
    // no-op once messages exist, so awaiting history before seeding the greeting avoids
    // the greeting winning the race and blocking hydration.
    void loadPersistedHistory().then(() => ensureGreeting(characterKey));
  }, [characterKey, setCharacter, ensureGreeting, loadPersistedHistory, isAuthenticated]);

  useEffect(() => () => cancel(), [cancel]);

  // --- Drag state ---
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null); // null = use CSS default
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    isDragging.current = true;
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onHandlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !panelRef.current) return;

    const pw = panelRef.current.offsetWidth;
    const ph = panelRef.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let nx = e.clientX - dragOffset.current.x;
    let ny = e.clientY - dragOffset.current.y;
    nx = Math.max(0, Math.min(nx, vw - pw));
    ny = Math.max(0, Math.min(ny, vh - ph));

    setPos({ x: nx, y: ny });
  }, []);

  const onHandlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Reset position when panel closes so it re-defaults on next open
  useEffect(() => {
    if (!isOpen) setPos(null);
  }, [isOpen]);

  // Hide when dismissed, hide entirely on the maximized page itself (that page renders
  // its own full chat — the widget stays globally mounted via RootLayout and would otherwise
  // float on top of the very page it just navigated to), and hide for signed-out visitors —
  // the concierge's history persistence only applies to members, so guests get nothing rather
  // than a chat that vanishes on refresh. `sessionReady` gates the flash while a stored token
  // is still being verified on load.
  if (dismissed || location.pathname === "/chat" || !sessionReady || !isAuthenticated) return null;

  const handleVenueClick = (venue: ConciergeVenue) => {
    navigate(`/${venue.slug}`);
    setIsOpen(false);
  };

  // Panel inline styles: when pos is set use top/left, otherwise fall back to CSS bottom-right
  const panelStyle: React.CSSProperties = pos
    ? { top: pos.y, left: pos.x, bottom: "auto", right: "auto" }
    : {};

  return (
    <>
      {/* -------- Collapsed avatar button -------- */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            setPulseRing(false);
          }}
          className="fixed bottom-6 right-6 z-[9997] group"
          aria-label="Open AI Concierge"
        >
          {pulseRing && (
            <>
              <span className="absolute inset-0 rounded-full bg-purple-500/30 animate-ping" />
              <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 animate-pulse" />
            </>
          )}

          <div className={`relative w-16 h-16 rounded-full overflow-hidden border-2 border-purple-500/50 transition-shadow duration-300 ${
            isLight
              ? "shadow-[0_0_20px_rgba(147,51,234,0.25),0_0_40px_rgba(236,72,153,0.15)] group-hover:shadow-[0_0_30px_rgba(147,51,234,0.4),0_0_50px_rgba(236,72,153,0.25)]"
              : "shadow-[0_0_20px_rgba(147,51,234,0.4),0_0_40px_rgba(236,72,153,0.2)] group-hover:shadow-[0_0_30px_rgba(147,51,234,0.6),0_0_50px_rgba(236,72,153,0.3)]"
          }`}
            style={selectedChar ? { borderColor: `${conciergeAccent}80` } : {}}
          >
            <ImageWithFallback
              src={conciergeAvatar}
              alt={`AI Concierge ${conciergeName}`}
              className={`w-full h-full ${selectedChar ? `object-contain ${isLight ? "bg-white" : "bg-[#0e0e18]"}` : "object-cover"}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 via-transparent to-transparent" />
            <div className={`absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center border-2 ${
              isLight ? "border-white" : "border-[#0a0a0f]"
            }`}>
              <Sparkles size={10} className="text-white" />
            </div>
          </div>

          <div className={`absolute left-20 bottom-2 whitespace-nowrap text-xs px-3 py-1.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none ${
            isLight
              ? "bg-white border border-black/10 text-zinc-900"
              : "bg-[#1a1a2e] border border-white/10 text-white"
          }`}>
            <span className="text-purple-400" style={selectedChar ? { color: conciergeAccent } : {}}>{conciergeName}</span>, {conciergeTitle}
            <div className={`absolute -left-1.5 bottom-2.5 w-3 h-3 rotate-45 ${
              isLight
                ? "bg-white border-l border-b border-black/10"
                : "bg-[#1a1a2e] border-l border-b border-white/10"
            }`} />
          </div>
        </button>
      )}

      {/* -------- Expanded draggable panel -------- */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed bottom-6 right-3 left-3 sm:left-auto sm:right-6 z-[9997] w-auto sm:w-[380px] max-w-[calc(100vw-1.5rem)]"
          style={panelStyle}
        >
          <div
            className={`relative border rounded-2xl overflow-hidden flex flex-col ${
              isLight
                ? "bg-white border-black/10 shadow-[0_8px_60px_rgba(0,0,0,0.12)]"
                : isGlassy
                  ? "bg-[#0e0e18]/70 backdrop-blur-2xl border-white/[0.12] shadow-[0_8px_60px_rgba(147,51,234,0.15),0_0_40px_rgba(255,51,102,0.1)]"
                  : "bg-[#0e0e18] border-white/[0.08] shadow-[0_8px_60px_rgba(147,51,234,0.15),0_0_40px_rgba(255,51,102,0.1)]"
            } ${isDragging.current ? (isLight ? "border-purple-300" : "border-purple-500/30") : ""}`}
          >
            {/* Header glow */}
            <div className={`absolute top-0 left-0 right-0 h-32 pointer-events-none ${
              isLight
                ? "bg-gradient-to-b from-purple-100/50 via-pink-50/30 to-transparent"
                : isGlassy
                  ? "bg-gradient-to-b from-purple-800/25 via-sky-900/10 to-transparent"
                  : "bg-gradient-to-b from-purple-900/30 via-pink-900/10 to-transparent"
            }`} />

            {/* ===== DRAG HANDLE ===== */}
            <div
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              className="relative flex items-center justify-end pt-2 pb-0 cursor-grab active:cursor-grabbing select-none touch-none shrink-0"
            >
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                isLight
                  ? "bg-black/[0.04]"
                  : "bg-white/[0.04]"
              }`}>
                <GripHorizontal size={14} className={isLight ? "text-zinc-400" : "text-gray-600"} />
                <span className={`text-[9px] uppercase tracking-widest font-medium ${
                  isLight ? "text-zinc-400" : "text-gray-600"
                }`}>
                  Drag to move
                </span>
              </div>
            </div>

            {/* Top bar */}
            <div className="relative flex items-center justify-between px-4 pt-2 pb-2 shrink-0">
              <div className="flex items-center gap-3">
                <div className={`relative w-12 h-12 rounded-full overflow-hidden border-2 border-purple-500/40 shadow-[0_0_15px_rgba(147,51,234,0.25)] ${
                  selectedChar && isLight ? "bg-white" : ""
                }`}
                  style={selectedChar ? { borderColor: `${conciergeAccent}60` } : {}}
                >
                  <ImageWithFallback
                    src={conciergeAvatar}
                    alt={conciergeName}
                    className={`w-full h-full ${selectedChar ? `object-contain ${isLight ? "bg-white" : "bg-[#0e0e18]"}` : "object-cover"}`}
                  />
                  <div className={`absolute bottom-0.5 right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 ${
                    isLight ? "border-white" : "border-[#0e0e18]"
                  }`} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm font-semibold ${
                      isLight ? "text-zinc-900" : "text-white"
                    }`}>
                      {conciergeName}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white uppercase tracking-wider">
                      AI
                    </span>
                  </div>
                  <p className={`text-[11px] ${
                    isLight ? "text-zinc-500" : "text-gray-500"
                  }`}>
                    Ask about venues, get real picks
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => navigate("/chat")}
                  aria-label="Maximize"
                  title="Maximize"
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    isLight
                      ? "bg-black/5 border border-black/10 text-zinc-500 hover:text-zinc-700 hover:bg-black/10"
                      : "bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <Maximize2 size={12} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    isLight
                      ? "bg-black/5 border border-black/10 text-zinc-500 hover:text-zinc-700 hover:bg-black/10"
                      : "bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <X size={13} />
                </button>
              </div>
            </div>

            <Suspense fallback={<div className="flex-1" />}>
              <ConciergePanel
                onVenueClick={handleVenueClick}
                isLight={isLight}
                isGlassy={isGlassy}
              />
            </Suspense>

            {/* Footer */}
            <div className="px-4 pb-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5">
                <Sparkles size={10} className="text-purple-400" />
                <span className={`text-[10px] ${
                  isLight ? "text-zinc-500" : "text-gray-600"
                }`}>
                  Powered by Listaa AI
                </span>
              </div>
              <button
                onClick={() => {
                  setDismissed(true);
                  setIsOpen(false);
                }}
                className={`text-[10px] transition-colors ${
                  isLight
                    ? "text-zinc-500 hover:text-zinc-700"
                    : "text-gray-600 hover:text-gray-400"
                }`}
              >
                Don't show again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
